import React, { useCallback, useMemo, useState } from "react";

import { notification } from "antd";
import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import { CustomFieldValueType } from "@mapmycustomers/shared/types/customField/CustomFieldValueType";
import {
  EntityTypesSupportingFieldCustomization,
  Group,
} from "@mapmycustomers/shared/types/entity";
import FunnelStageValue from "@mapmycustomers/shared/types/FunnelStageValue";
import { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import LayoutSchemaFieldDefaultValue from "@mapmycustomers/shared/types/layout/LayoutSchemaFieldDefaultValue";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";
import { Button, Labeled, Modal } from "@mapmycustomers/ui";

import FrequencyIntervalValue from "@app/component/frequency/type/FrequencyIntervalValue";
import CustomFieldsEditor from "@app/component/view/SelectBar/components/BulkEditButton/component/CustomFieldsEditor";
import GeneralFieldsEditor from "@app/component/view/SelectBar/components/BulkEditButton/component/GeneralFieldsEditor";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCustomField } from "@app/util/fieldModel/impl/assert";

import buildCustomFieldValue from "../../util/buildCustomFieldValue";

import styles from "./EditDefaultValueModal.module.scss";
import messages from "./messages";

interface Props {
  defaultFunnelStage?: Partial<FunnelStageValue>;
  entityType: EntityTypesSupportingFieldCustomization;
  groups: Group[];
  onDefaultValueChange?: (value: LayoutSchemaFieldDefaultValue) => void;
  onHide?: () => void;
  schemaField: LayoutSchemaField;
}

const EditDefaultValueModal: React.FC<Props> = ({
  defaultFunnelStage,
  entityType,
  groups,
  onDefaultValueChange,
  onHide,
  schemaField,
}) => {
  const intl = useIntl();
  const analyticsIssuer = useAnalytics();
  const field = getFieldModelByEntityType(entityType).getByPlatformName(schemaField.field);

  const isFrequencyInterval = field?.hasFeature(FieldFeature.FREQUENCY_INTERVAL);

  const [currentDefaultValue, setCurrentDefaultValue] = useState<
    [FrequencyIntervalValue] | LayoutSchemaFieldDefaultValue
  >(() => {
    if (isFrequencyInterval) {
      let frequencyIntervalValue: FrequencyIntervalValue | undefined;
      if (isNotEmpty(schemaField.defaultValue)) {
        frequencyIntervalValue = {
          cadenceInterval: schemaField.defaultValue[0] as FrequencyIntervalValue["cadenceInterval"],
        };
      }
      return frequencyIntervalValue ? [frequencyIntervalValue] : undefined;
    }

    return schemaField.field === "funnelId"
      ? [defaultFunnelStage as Partial<FunnelStageValue>]
      : schemaField.defaultValue;
  });

  const isRemoveDefaultVisible = useMemo(() => {
    if (field?.hasFeature(FieldFeature.FREQUENCY_INTERVAL)) {
      return false;
    }

    return schemaField.field === "funnelId"
      ? (currentDefaultValue?.[0] as Partial<FunnelStageValue>)?.funnel ||
          (currentDefaultValue?.[0] as Partial<FunnelStageValue>)?.stage
      : currentDefaultValue?.length;
  }, [currentDefaultValue, field, schemaField.field]);

  const isSaveButtonDisabled = useMemo(() => {
    return schemaField.field === "funnelId"
      ? defaultFunnelStage === currentDefaultValue?.[0]
      : schemaField.defaultValue === currentDefaultValue;
  }, [currentDefaultValue, defaultFunnelStage, schemaField]);

  const handleSave = useCallback(() => {
    analyticsIssuer?.clicked([
      schemaField.defaultValue?.length ? "Edit Default Value" : "Set Default Value",
    ]);
    notification.success({
      message: intl.formatMessage(messages.successNotification),
    });

    let defaultValue: LayoutSchemaFieldDefaultValue;
    if (isFrequencyInterval) {
      const frequencyIntervalValue = currentDefaultValue?.[0] as FrequencyIntervalValue | undefined;
      defaultValue = frequencyIntervalValue?.cadenceInterval
        ? [frequencyIntervalValue.cadenceInterval]
        : undefined;
    } else {
      defaultValue = currentDefaultValue as LayoutSchemaFieldDefaultValue;
    }

    onDefaultValueChange?.(defaultValue);
    onHide?.();
  }, [
    analyticsIssuer,
    currentDefaultValue,
    intl,
    isFrequencyInterval,
    onDefaultValueChange,
    onHide,
    schemaField,
  ]);

  const handleRemove = useCallback(() => {
    setCurrentDefaultValue(undefined);
    analyticsIssuer?.clicked(["Remove default value"]);
  }, [analyticsIssuer]);

  const handleChange = useCallback(
    (value) => {
      if (schemaField.fieldType === SchemaFieldType.GROUP) {
        setCurrentDefaultValue(Array.from(value) as LayoutSchemaFieldDefaultValue);
      } else {
        setCurrentDefaultValue([value]);
      }
    },
    [schemaField]
  );

  if (!field) {
    return null;
  }

  return (
    <Modal
      className={styles.container}
      closable
      destroyOnClose
      footer={[
        <Button disabled={isSaveButtonDisabled} key="submit" onClick={handleSave} type="primary">
          {intl.formatMessage(
            schemaField.defaultValue?.length ? messages.saveChanges : messages.setDefaultValue
          )}
        </Button>,
      ]}
      onCancel={onHide}
      open
      title={intl.formatMessage(messages.title)}
    >
      <div className={styles.content}>
        <div className={styles.text}>
          {intl.formatMessage(messages.description, {
            strong: (text: string) => <strong>{text}</strong>,
          })}
        </div>
        <div className={styles.inputRow}>
          <div className={styles.input}>
            <Labeled label={field.displayName}>
              {isCustomField(field) ? (
                <CustomFieldsEditor
                  customField={field}
                  disableLabel
                  onChange={(value) =>
                    setCurrentDefaultValue(value.value as LayoutSchemaFieldDefaultValue)
                  }
                  value={buildCustomFieldValue(
                    field.customFieldData,
                    currentDefaultValue as CustomFieldValueType
                  )}
                />
              ) : (
                <GeneralFieldsEditor
                  disableLabel
                  entityType={entityType}
                  field={field}
                  groups={groups}
                  isStageRequired={false}
                  onChange={handleChange}
                  showFrequencyIntervalActivityTypes={false}
                  value={
                    currentDefaultValue?.length
                      ? schemaField.fieldType === SchemaFieldType.GROUP
                        ? currentDefaultValue
                        : currentDefaultValue[0]
                      : undefined
                  }
                />
              )}
            </Labeled>
          </div>
          {isRemoveDefaultVisible ? (
            <Button key="submit" onClick={handleRemove} type="default">
              {intl.formatMessage(messages.removeDefault)}
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default EditDefaultValueModal;
